import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import logo from "../../../Assets/Images/logo.png";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import axios from "axios";
import { MAIN_URL, getApiHeaders } from "../../../Configurations/Urls";
import { Eye, EyeOff } from "lucide-react";
import {
  getSelfAttendanceOrganizations,
  getManagerAttendanceOrganizations,
  navigateAfterAttendanceLogin,
  navigateAfterManagerLogin,
  isInternAttendanceUser,
  clearAttendanceLandingState,
  assertManagerAttendanceLoginAllowed,
  resolveDefaultAttendanceOrganization,
  shouldUseManagerKioskFlow,
  canAccessAttendanceLogin,
  ensureOrganizationsList,
} from "../../../Utils/attendanceAuth";
import {
  activateOrganizationAndHydrate,
} from "../../../Utils/attendanceSession";
import {
  applyPreferredOrganizationToUserPayload,
  clearClientPreferredOrganizationId,
} from "../../../Utils/clientActiveOrganization";

function Login({
  title = "User Login",
  appName = "Attendance",
  description,
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { isLoggedIn, login, logout, setAccess, setMode } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    const savedEmail = localStorage.getItem("savedEmail");
    const savedPassword = localStorage.getItem("savedPassword");

    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const completeLoginForOrganization = async (
    organizationId,
    session
  ) => {
    if (!session?.user || !session?.token) return;

    setLoading(true);
    try {
      const sessionUser = await activateOrganizationAndHydrate(
        organizationId,
        session.user.application_user_id
      );

      if (shouldUseManagerKioskFlow(sessionUser)) {
        const managerDenial = assertManagerAttendanceLoginAllowed(sessionUser);
        if (managerDenial) {
          toast.error(managerDenial);
          return;
        }

        login(sessionUser);
        setAccess(sessionUser?.role?.system_role_name);
        toast.success("Login successful!");
        navigateAfterManagerLogin(sessionUser, navigate);
        return;
      }

      if (!canAccessAttendanceLogin(sessionUser)) {
        toast.error(
          isInternAttendanceUser(sessionUser)
            ? "Intern record not found for this organization. Contact your HR admin."
            : "Employee record not found for this organization. Contact your HR admin."
        );
        return;
      }

      login(sessionUser);
      setAccess(sessionUser?.role?.system_role_name);
      toast.success("Login successful!");

      const navigated = navigateAfterAttendanceLogin(
        sessionUser,
        navigate,
        setMode
      );
      if (!navigated) {
        toast.error("Could not open your attendance page. Please try again.");
      }
    } catch (error) {
      const apiMessage = error.response?.data?.message;
      toast.error(apiMessage || "Failed to set active organization.");
    } finally {
      setLoading(false);
    }
  };

  const completeManagerLogin = async (session) => {
    if (!session?.token || !session?.user) return;

    setLoading(true);
    try {
      let sessionUser = ensureOrganizationsList(session.user);
      const orgs = getManagerAttendanceOrganizations(sessionUser);

      if (!orgs.length) {
        toast.error("No organizations found for this admin account.");
        return;
      }

      if (orgs.length === 1) {
        const defaultOrg = resolveDefaultAttendanceOrganization(
          sessionUser,
          orgs
        );
        sessionUser = await activateOrganizationAndHydrate(
          defaultOrg.organization_id,
          sessionUser.application_user_id
        );
      }

      const managerDenial = assertManagerAttendanceLoginAllowed(sessionUser);
      if (managerDenial) {
        toast.error(managerDenial);
        return;
      }

      login(sessionUser);
      setAccess(sessionUser?.role?.system_role_name);
      toast.success("Login successful!");
      navigateAfterManagerLogin(sessionUser, navigate);
    } catch (error) {
      const apiMessage = error.response?.data?.message;
      toast.error(apiMessage || "Failed to complete login.");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const formErrors = {};
    let isValid = true;

    if (!email) {
      formErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      formErrors.email = "Invalid email format";
      isValid = false;
    }

    if (!password) {
      formErrors.password = "Password is required";
      isValid = false;
    } else if (password.length < 6) {
      formErrors.password = "Password must be at least 6 characters";
      isValid = false;
    }

    setErrors(formErrors);
    return isValid;
  };

  const submitLogin = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (rememberMe) {
      localStorage.setItem("savedEmail", email);
      localStorage.setItem("savedPassword", password);
    } else {
      localStorage.removeItem("savedEmail");
      localStorage.removeItem("savedPassword");
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${MAIN_URL}/api/auth/login`,
        {
          email,
          password_hash: password,
        },
        { headers: getApiHeaders() }
      );

      const { token, user } = response.data;

      localStorage.setItem("token", token);
      setAccess(user?.role?.system_role_name);
      clearClientPreferredOrganizationId();

      const { data: tokenData } = await axios.get(
        `${MAIN_URL}/api/auth/token-login`,
        { headers: getApiHeaders() }
      );
      const hydratedUser = ensureOrganizationsList(
        applyPreferredOrganizationToUserPayload(tokenData?.user || user)
      );

      if (shouldUseManagerKioskFlow(hydratedUser)) {
        const managerDenial = assertManagerAttendanceLoginAllowed(hydratedUser);
        if (managerDenial) {
          localStorage.removeItem("token");
          clearAttendanceLandingState();
          toast.error(managerDenial);
          return;
        }

        await completeManagerLogin({
          token,
          user: hydratedUser,
        });
        return;
      }

      const organizations = getSelfAttendanceOrganizations(hydratedUser);

      if (!organizations.length) {
        localStorage.removeItem("token");
        clearAttendanceLandingState();
        toast.error(
          isInternAttendanceUser(hydratedUser)
            ? "Attendance login is only available for users registered as Intern in an organization."
            : "Attendance login is only available for users registered as Employee in an organization."
        );
        return;
      }

      if (organizations.length === 1) {
        await completeLoginForOrganization(organizations[0].organization_id, {
          token,
          user: hydratedUser,
        });
        return;
      }

      login(hydratedUser);
      setAccess(hydratedUser?.role?.system_role_name);
      toast.success("Login successful!");
      clearAttendanceLandingState();
      navigate("/organization-toggle", { replace: true });
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      const apiMessage =
        typeof data?.message === "string"
          ? data.message
          : Array.isArray(data?.messages)
            ? data.messages.join(" ")
            : null;

      if (data?.errors) {
        setErrors(data.errors);
      } else if (apiMessage) {
        toast.error(apiMessage);
      } else if (status === 401 || status === 403) {
        toast.error("Login failed. Please check your credentials or HR access.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      navigate("/", { replace: true });
      return;
    }
    if (isLoggedIn && !token) {
      logout();
    }
  }, [isLoggedIn, logout, navigate]);

  return (
    <div className="mainSection position-relative">
      <div className="row g-0">
        <div className="col-md-6">
          <div className="whiteBox">
            <div className="panel panel-default">
              <div className="panel-heading text-center">
                <img src={logo} className="img-fluid mb-2" alt="Logo" />
                <h2>{title}</h2>
              </div>
              <div className="panel-body">
                <form onSubmit={submitLogin}>
                  <div className="form-group mb-3">
                    <input
                      placeholder="Enter Your Email"
                      type="text"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`form-control ${errors.email ? "is-invalid" : ""}`}
                    />
                    {errors.email && (
                      <div className="invalid-feedback">{errors.email}</div>
                    )}
                  </div>
                  <div className="form-group mb-3 position-relative">
                    <input
                      placeholder="Enter Password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`form-control pe-5 ${errors.password ? "is-invalid" : ""}`}
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      className="position-absolute top-50 translate-middle-y"
                      style={{
                        right: "15px",
                        cursor: "pointer",
                        color: "#6c757d",
                      }}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </span>
                    {errors.password && (
                      <div className="invalid-feedback">{errors.password}</div>
                    )}
                  </div>

                  <div className="form-check mt-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="rememberMe"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="rememberMe">
                      Remember Password
                    </label>
                  </div>

                  <input
                    type="submit"
                    className="btn btn-primary w-40"
                    value={loading ? "Signing in..." : "Login"}
                    disabled={loading}
                  />

                  <div className="mt-3">
                    Forgot Password?{" "}
                    <Link to="/forgot-password" className="text-primary">
                      Forgot Password
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-6 d-none d-md-block">
          <div className="darkBox">
            <div className="DarkPanel text-white p-4">
              <h1>{appName}</h1>
              <p>
                {description ||
                  "Mark and manage attendance with the same secure login used across HR, EMS, and PM."}
              </p>
              <p className="joinedLine mt-3">Trusted by thousands of users.</p>
              <h6 className="mt-4 text-light-emphasis">
                &copy; {new Date().getFullYear()} Nest Software.
              </h6>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
